import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, TextField, Button, Typography, Paper, Box,
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Checkbox, FormControlLabel, Tabs, Tab, Alert, useTheme, useMediaQuery,
    Grid, Avatar, LinearProgress, Divider, InputAdornment, IconButton,
    Stack, Chip
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import ACCOUNT_PLANS from '../config/accountPlans';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null;

function TabPanel({ children, value, index, ...other }) {
    const isActive = value === index;

    return (
        <Box
            role="tabpanel"
            hidden={!isActive}
            id={`account-settings-tabpanel-${index}`}
            aria-labelledby={`account-settings-tab-${index}`}
            sx={{
                width: '100%',
                height: '100%',
                overflowY: 'auto',
                flexGrow: 1,
                display: isActive ? 'block' : 'none',
            }}
            {...other}
        >
            {isActive && <Box sx={{ width: '100%', pt: 2 }}>{children}</Box>}
        </Box>
    );
}

const a11yProps = (index) => ({
    id: `account-settings-tab-${index}`,
    'aria-controls': `account-settings-tabpanel-${index}`,
});

const AccountSettings = ({ isDialog = false, open = false, onClose }) => {
    const token = useSelector((state) => state.auth.token);
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    // Profile State
    const [userInfo, setUserInfo] = useState(null);
    const [editedProfile, setEditedProfile] = useState({});
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);

    // Settings State
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
    const [notifPrefs, setNotifPrefs] = useState({
        emailNotifications: true,
        pushNotifications: false,
        summaryFrequencyHours: 12,
    });

    // Subscription State
    const [subscription, setSubscription] = useState(null);
    const [appUsage, setAppUsage] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
    const [isProcessingPortal, setIsProcessingPortal] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const setFeedbackMessage = useCallback((text, type = 'success') => {
        setMessage(text);
        setMessageType(type);
    }, []);

    // Fetch user data
    const fetchUserData = useCallback(async () => {
        if (!token) return;
        setLoadingData(true);
        try {
            const [userRes, appRes] = await Promise.all([
                axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/integrations/status', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            const data = userRes.data;

            if (appRes.data) {
                setAppUsage(appRes.data);
            }

            // Set User Info
            setUserInfo(data);
            setEditedProfile({
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                company: data.company || '',
                occupation: data.occupation || '',
                website: data.website || '',
                linkedInProfile: data.linkedInProfile || '',
                twitter: data.twitter || '',
                github: data.github || '',
                instagram: data.instagram || '',
                availability: data.availability || '',
            });

            // Set Settings
            if (typeof data.twoFactorEnabled === 'boolean') setTwoFactorEnabled(data.twoFactorEnabled);

            // Set Subscription
            setSubscription(data.subscription || null);

        } catch (err) {
            console.error('Error fetching data:', err);
            setFeedbackMessage('Error fetching account data.', 'error');
        } finally {
            setLoadingData(false);
        }
    }, [token, setFeedbackMessage]);

    // Fetch Notification Settings
    useEffect(() => {
        const fetchSettings = async () => {
            if (!token) return;
            try {
                const res = await axios.get('/api/user-settings', { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.notificationPreferences) {
                    setNotifPrefs(res.data.notificationPreferences);
                }
            } catch (err) {
                console.error('Error fetching settings:', err);
            }
        };
        fetchSettings();
    }, [token]);

    const fetchInvoices = useCallback(async () => {
        if (!token) return;
        setLoadingInvoices(true);
        try {
            const res = await axios.get('/api/billing/invoices', { headers: { Authorization: `Bearer ${token}` } });
            setInvoices(res.data.invoices || []);
        } catch (err) {
            console.error('Error fetching invoices:', err);
        } finally {
            setLoadingInvoices(false);
        }
    }, [token]);

    useEffect(() => {
        if (open || !isDialog) {
            fetchUserData();
            fetchInvoices();
        }
    }, [open, isDialog, fetchUserData, fetchInvoices]);

    const handleProfileChange = (e) => {
        setEditedProfile({ ...editedProfile, [e.target.name]: e.target.value });
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const saveProfile = async () => {
        try {
            const formData = new FormData();
            Object.keys(editedProfile).forEach(key => {
                formData.append(key, editedProfile[key]);
            });
            if (avatarFile) {
                formData.append('avatar', avatarFile);
            }

            const res = await axios.put('/api/users/me', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                },
            });

            setUserInfo(res.data.user);
            setFeedbackMessage('Profile updated successfully.', 'success');
            // Check if we need to update the global user state via Redux if applicable, 
            // but effectively we just updated local state.
        } catch (err) {
            console.error('Error updating profile:', err);
            setFeedbackMessage('Error updating profile.', 'error');
        }
    };

    const saveNotifications = async () => {
        try {
            await axios.put('/api/user-settings/notifications', notifPrefs, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFeedbackMessage('Notification settings saved.', 'success');
        } catch (err) {
            console.error('Error saving notification settings:', err);
            setFeedbackMessage('Error saving notification settings.', 'error');
        }
    };

    const toggleTwoFactor = async (enabled) => {
        try {
            const endpoint = enabled ? '/api/users/enable-2fa' : '/api/users/disable-2fa';
            await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
            setTwoFactorEnabled(enabled);
            setFeedbackMessage(`Two-factor authentication ${enabled ? 'enabled' : 'disabled'}.`, 'success');
        } catch (err) {
            console.error('Error updating 2FA:', err);
            setFeedbackMessage('Error updating 2FA settings.', 'error');
        }
    };

    // Billing Handlers
    const handleCheckout = useCallback(async () => {
        setIsProcessingCheckout(true);
        setMessage('');
        try {
            const dashboardUrl = `${window.location.origin}/#/dashboard`;
            const successUrl = `${dashboardUrl}?checkoutSuccess=true&session_id={CHECKOUT_SESSION_ID}`;
            const cancelUrl = `${dashboardUrl}?checkoutCanceled=true`;
            const { data } = await axios.post('api/billing/checkout', { successUrl, cancelUrl }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (stripePromise && data.id) {
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
                if (error) throw error;
            } else if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (err) {
            console.error('Checkout error:', err);
            setFeedbackMessage(err.response?.data?.error || 'Unable to start checkout.', 'error');
        } finally {
            setIsProcessingCheckout(false);
        }
    }, [token, setFeedbackMessage]);

    const handleManageBilling = useCallback(async () => {
        setIsProcessingPortal(true);
        try {
            const returnUrl = `${window.location.origin}/#/dashboard`;
            const { data } = await axios.post('api/billing/portal', { returnUrl }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (data.url) window.location.href = data.url;
            else throw new Error('No portal URL');
        } catch (err) {
            console.error('Portal error:', err);
            setFeedbackMessage('Unable to open billing portal.', 'error');
        } finally {
            setIsProcessingPortal(false);
        }
    }, [token, setFeedbackMessage]);

    // Helpers for rendering
    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    if (isDialog && !open) {
        return null;
    }

    if (loadingData && !userInfo) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    }

    return (
        <Dialog
            open={isDialog ? open : true}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { bgcolor: 'background.default', minHeight: '80vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography variant="h5" fontWeight="bold">Account Settings</Typography>
                {isDialog && <Button onClick={onClose}>Close</Button>}
            </DialogTitle>
            <DialogContent dividers sx={{ p: isSmallScreen ? 2 : 4 }}>
                <Grid container spacing={3}>
                    {/* LEFT COLUMN: Profile & Socials */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }} elevation={2}>
                            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                                <Avatar
                                    src={
                                        avatarPreview ||
                                        (userInfo?.avatarUrl
                                            ? `/api/users/avatars/${userInfo._id}/${userInfo.avatarUrl.split('/').pop()}`
                                            : '')
                                    }
                                    sx={{ width: 100, height: 100, mx: 'auto', border: `3px solid ${theme.palette.primary.main}` }}
                                />
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="icon-button-file"
                                    type="file"
                                    onChange={handleAvatarChange}
                                />
                                <label htmlFor="icon-button-file">
                                    <IconButton color="primary" aria-label="upload picture" component="span" sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}>
                                        <PhotoCamera />
                                    </IconButton>
                                </label>
                            </Box>

                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                User ID
                            </Typography>
                            <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                                {userInfo?._id}
                            </Box>

                            <Typography variant="h6" gutterBottom>{userInfo?.username}</Typography>
                            <Typography color="text.secondary" gutterBottom>{userInfo?.email}</Typography>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" align="left" gutterBottom>Social Links</Typography>
                            <TextField
                                fullWidth size="small" margin="dense"
                                label="Website" name="website"
                                value={editedProfile.website} onChange={handleProfileChange}
                                InputProps={{ startAdornment: <InputAdornment position="start">🌐</InputAdornment> }}
                            />
                            <TextField
                                fullWidth size="small" margin="dense"
                                label="LinkedIn" name="linkedInProfile"
                                value={editedProfile.linkedInProfile} onChange={handleProfileChange}
                                InputProps={{ startAdornment: <InputAdornment position="start">in</InputAdornment> }}
                            />
                            <TextField
                                fullWidth size="small" margin="dense"
                                label="Twitter" name="twitter"
                                value={editedProfile.twitter} onChange={handleProfileChange}
                                InputProps={{ startAdornment: <InputAdornment position="start">𝕏</InputAdornment> }}
                            />

                        </Paper>

                        <Paper sx={{ p: 3 }} elevation={2}>
                            <Typography variant="h6" gutterBottom>Danger Zone</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Permanently delete your account and all data.
                            </Typography>
                            <Button
                                color="error"
                                variant="outlined"
                                fullWidth
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                Delete Account
                            </Button>
                        </Paper>
                    </Grid>

                    {/* RIGHT COLUMN: Edit Profile, Usage, Billing, Settings */}
                    <Grid item xs={12} md={8}>
                        {/* 1. Personal Information */}
                        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6">Personal Information</Typography>
                                <Button variant="contained" onClick={saveProfile}>Save Changes</Button>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="First Name" name="firstName" value={editedProfile.firstName} onChange={handleProfileChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Last Name" name="lastName" value={editedProfile.lastName} onChange={handleProfileChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Company" name="company" value={editedProfile.company} onChange={handleProfileChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Occupation" name="occupation" value={editedProfile.occupation} onChange={handleProfileChange} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Availability Status" name="availability" value={editedProfile.availability} onChange={handleProfileChange} placeholder="e.g. Available for freelance" />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* 2. Usage Statistics */}
                        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                            <Typography variant="h6" gutterBottom>Usage Statistics</Typography>

                            {/* Workspace translated words (human + TM + AI) */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Translated words (this month)</Typography>
                                    <Typography variant="body2">
                                        {(userInfo?.translatedWordsUsage || 0).toLocaleString()} / {ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.translatedWords === 9007199254740991 ? 'Unlimited' : (ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.translatedWords ?? 0).toLocaleString()} words
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.translatedWords === 9007199254740991 ? 100 : Math.min(((userInfo?.translatedWordsUsage || 0) / (ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.translatedWords || 1)) * 100, 100)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>

                            {/* AI / machine-translation words (the credit-costing subset) */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">AI translation words (this month)</Typography>
                                    <Typography variant="body2">
                                        {(userInfo?.deeplUsage || 0).toLocaleString()} / {ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.aiWords === 9007199254740991 ? 'Unlimited' : (ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.aiWords ?? 0).toLocaleString()} words
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.aiWords === 9007199254740991 ? 100 : Math.min(((userInfo?.deeplUsage || 0) / (ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.limits?.aiWords || 1)) * 100, 100)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Storage Usage</Typography>
                                    <Typography variant="body2">
                                        {formatBytes(userInfo?.storageUsed)} / {ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.storageLimit === 9007199254740991 ? 'Unlimited' : formatBytes(ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.storageLimit)}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.storageLimit === 9007199254740991 ? 100 : Math.min((userInfo?.storageUsed / (ACCOUNT_PLANS[userInfo?.accountType?.toLowerCase() || 'free']?.storageLimit || 1)) * 100, 100)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>

                            {appUsage && appUsage.usage && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2">Connected Apps</Typography>
                                        <Typography variant="body2">
                                            {appUsage.usage.limit === 999999 ? 'Unlimited' : `${appUsage.usage.count} / ${appUsage.usage.limit}`}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={appUsage.usage.limit === 999999 ? 0 : Math.min((appUsage.usage.count / appUsage.usage.limit) * 100, 100)}
                                        sx={{ height: 8, borderRadius: 4, mb: 2 }}
                                        color={appUsage.usage.count >= appUsage.usage.limit ? 'error' : 'primary'}
                                    />
                                </Box>
                            )}

                            {appUsage && appUsage.paidApps && appUsage.paidApps.length > 0 && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>Active App Subscriptions</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={appUsage.usage?.cancelAtPeriodEnd ? 1 : 0}>
                                        {appUsage.paidApps.map(app => (
                                            <Chip
                                                key={app}
                                                label={app.toUpperCase()}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Stack>
                                    {appUsage.usage?.cancelAtPeriodEnd && (
                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                                            Scheduled to cancel on {new Date(appUsage.usage.currentPeriodEnd).toLocaleDateString()}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Paper>

                        {/* 3. Subscription */}
                        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                            <Typography variant="h6" gutterBottom>Subscription</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Current Plan: {userInfo?.accountType?.toUpperCase() || 'FREE'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Status: {subscription?.status || 'Active'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            if (onClose) onClose();
                                            navigate('/dashboard/pricing');
                                        }}
                                        sx={{ mr: 1 }}
                                    >
                                        Upgrade
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleManageBilling}
                                        disabled={!subscription?.status || isProcessingPortal}
                                    >
                                        Manage
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>

                        {/* 3.5 Billing History / Invoices */}
                        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                            <Typography variant="h6" gutterBottom>Billing History</Typography>
                            {loadingInvoices ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                            ) : invoices.length > 0 ? (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                <th style={{ padding: '8px 4px' }}>Date</th>
                                                <th style={{ padding: '8px 4px' }}>Invoice</th>
                                                <th style={{ padding: '8px 4px' }}>Amount</th>
                                                <th style={{ padding: '8px 4px' }}>Status</th>
                                                <th style={{ padding: '8px 4px', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map((inv) => (
                                                <tr key={inv.id} style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <td style={{ padding: '12px 4px' }}>{new Date(inv.date).toLocaleDateString()}</td>
                                                    <td style={{ padding: '12px 4px' }}>{inv.number}</td>
                                                    <td style={{ padding: '12px 4px' }}>{inv.amount.toLocaleString(undefined, { style: 'currency', currency: inv.currency })}</td>
                                                    <td style={{ padding: '12px 4px' }}>
                                                        <Chip 
                                                            label={inv.status.toUpperCase()} 
                                                            size="small" 
                                                            color={inv.status === 'paid' ? 'success' : 'default'} 
                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px 4px', textAlign: 'right' }}>
                                                        <Button size="small" variant="text" href={inv.pdf} target="_blank" sx={{ minWidth: 'auto', p: 0.5 }}>PDF</Button>
                                                        <Button size="small" variant="text" href={inv.url} target="_blank" sx={{ minWidth: 'auto', p: 0.5, ml: 1 }}>View</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No invoice history found.</Typography>
                            )}
                        </Paper>

                        {/* 4. Settings */}
                        <Paper sx={{ p: 3 }} elevation={2}>
                            <Typography variant="h6" gutterBottom>Notification & Security</Typography>
                            <FormControlLabel
                                control={<Checkbox checked={notifPrefs.emailNotifications} onChange={(e) => setNotifPrefs({ ...notifPrefs, emailNotifications: e.target.checked })} />}
                                label="Email Notifications"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={notifPrefs.pushNotifications} onChange={(e) => setNotifPrefs({ ...notifPrefs, pushNotifications: e.target.checked })} />}
                                label="Push Notifications"
                            />
                            <Divider sx={{ my: 1 }} />
                            <FormControlLabel
                                control={<Checkbox checked={twoFactorEnabled} onChange={(e) => toggleTwoFactor(e.target.checked)} />}
                                label="Enable Two-Factor Authentication"
                            />
                            <Box sx={{ mt: 2 }}>
                                <Button size="small" variant="outlined" onClick={saveNotifications}>Save Preferences</Button>
                            </Box>
                        </Paper>

                    </Grid>
                </Grid>
            </DialogContent>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Account Deletion</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Type <strong>DELETE</strong> to confirm you want to permanently remove your account.
                    </Typography>
                    <TextField autoFocus margin="dense" label="Type DELETE" fullWidth value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        disabled={confirmText !== 'DELETE'}
                        onClick={async () => {
                            try {
                                await axios.delete('/api/users/me', { headers: { Authorization: `Bearer ${token}` } });
                                window.location.reload(); // Force reload to clear state/redirect
                            } catch (err) {
                                setFeedbackMessage('Error deleting account', 'error');
                            }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {message && (
                <Alert severity={messageType} sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>
                    {message}
                </Alert>
            )}
        </Dialog>
    );
};

export default AccountSettings;
